import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { CsvDownloadButton } from '@/components/venue/CsvDownloadButton'
import { NoticeReadStatusSummaryCard } from '@/components/venue/NoticeReadStatusSummaryCard'
import { PromotionPerformanceListTable } from '@/components/venue/PromotionPerformanceListTable'
import { PromotionPerformanceSummaryCard } from '@/components/venue/PromotionPerformanceSummaryCard'
import { useCampaignPerformance } from '@/hooks/useCampaignPerformance'
import { useCampaignPerformanceSummary } from '@/hooks/useCampaignPerformanceSummary'
import { useNoticeReadStatus } from '@/hooks/useNoticeReadStatus'
import { cn } from '@/lib/utils/cn'
import { todayDateKey } from '@/lib/venue/calendarGrid'
import { downloadCampaignPerformanceCsv } from '@/lib/venue/campaignPerformanceApi'
import {
  CHARGE_STATUS_FILTER_OPTIONS,
  defaultPerformancePeriod,
  validatePerformancePeriod,
  type PerformanceListFilter,
} from '@/lib/venue/campaignPerformanceViewModel'
import { useVenueContext } from '@/routes/venueContext'

type Tab = 'PERFORMANCE' | 'NOTICE_READ'
type ChargeStatusFilter = NonNullable<PerformanceListFilter['chargeStatus']> | 'ALL'

function initialFilter(): PerformanceListFilter {
  return defaultPerformancePeriod(todayDateKey())
}

/**
 * 홍보 성과·비용 조회 — 'notices/performance'(설계 §2, #738). 탭 2개(홍보 성과·비용 / 운영 공지
 * 열람 현황).
 *
 * 🚨 발송 완료(`SENT`) 성과만 다룬다 — `#736` §1이 경고한 대로 발송 상태(예약·차단)와 과금 상태는
 * 서로 다른 EP가 소유하고 커서가 호환되지 않아, 이 화면은 상태 칩을 만드는 대신 예약·차단 상태를
 * 기존 `notices/history` 화면으로 안내한다(설계 §자율 결정 1).
 */
export function VenuePromotionPerformancePage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()

  const [tab, setTab] = useState<Tab>('PERFORMANCE')
  const [appliedFilter, setAppliedFilter] = useState<PerformanceListFilter>(initialFilter)
  const [dateInputs, setDateInputs] = useState({ from: appliedFilter.from, to: appliedFilter.to })
  const [chargeStatusFilter, setChargeStatusFilter] = useState<ChargeStatusFilter>('ALL')

  const periodError = validatePerformancePeriod(dateInputs)
  const period = { from: appliedFilter.from, to: appliedFilter.to }

  const summaryQuery = useCampaignPerformanceSummary(venueId, period, { enabled: tab === 'PERFORMANCE' })
  const listQuery = useCampaignPerformance(venueId, appliedFilter, { enabled: tab === 'PERFORMANCE' })
  const noticeReadStatusQuery = useNoticeReadStatus(venueId, period, { enabled: tab === 'NOTICE_READ' })

  const pages = listQuery.data?.pages ?? []
  const items = pages.flatMap((page) => page.campaigns)
  const conversionTracking = pages[0]?.conversionTracking

  function applyFilter(nextChargeStatus: ChargeStatusFilter) {
    if (periodError) return
    setAppliedFilter({
      from: dateInputs.from,
      to: dateInputs.to,
      chargeStatus: nextChargeStatus === 'ALL' ? undefined : nextChargeStatus,
    })
  }

  return (
    <>
      <PageHeader
        title="홍보 성과·비용 조회"
        description="발송 완료된 지역 홍보의 열람률·전환·비용을 조회합니다. 예약·차단 상태는 발송 이력에서 확인하세요."
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="performance-from">
            시작일
          </label>
          <Input
            id="performance-from"
            type="date"
            value={dateInputs.from}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, from: event.target.value }))
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="performance-to">
            종료일
          </label>
          <Input
            id="performance-to"
            type="date"
            value={dateInputs.to}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, to: event.target.value }))
            }}
          />
        </div>
        <Button size="md" disabled={Boolean(periodError)} onClick={() => applyFilter(chargeStatusFilter)}>
          조회
        </Button>

        {tab === 'PERFORMANCE' ? (
          <div className="ml-auto flex flex-col items-end gap-1">
            <CsvDownloadButton
              label="성과 CSV 다운로드"
              onDownload={() => downloadCampaignPerformanceCsv(venueId, appliedFilter)}
            />
            <p className="max-w-xs text-right text-xs text-slateish-400">
              다운로드한 파일 마지막 줄에 오류 표시가 있는지 확인해 주세요.
            </p>
          </div>
        ) : null}
      </div>

      {periodError ? <p className="mb-3 text-xs text-danger-500">{periodError}</p> : null}

      <div role="tablist" aria-label="성과 조회 탭" className="mb-4 flex gap-2">
        {(
          [
            { value: 'PERFORMANCE', label: '홍보 성과·비용' },
            { value: 'NOTICE_READ', label: '운영 공지 열람 현황' },
          ] as { value: Tab; label: string }[]
        ).map((option) => {
          const selected = tab === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                setTab(option.value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {tab === 'PERFORMANCE' ? (
        <>
          {summaryQuery.isLoading ? <LoadingState rows={1} label="성과 요약을 불러오는 중" /> : null}
          {summaryQuery.isError ? (
            <ErrorState
              error={summaryQuery.error}
              onRetry={() => {
                void summaryQuery.refetch()
              }}
            />
          ) : null}
          {summaryQuery.data ? <PromotionPerformanceSummaryCard data={summaryQuery.data} /> : null}

          <div role="group" aria-label="과금 상태 필터" className="my-4 flex flex-wrap gap-2">
            {(
              [{ value: 'ALL', label: '전체' }, ...CHARGE_STATUS_FILTER_OPTIONS] as {
                value: ChargeStatusFilter
                label: string
              }[]
            ).map((option) => {
              const selected = chargeStatusFilter === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setChargeStatusFilter(option.value)
                    applyFilter(option.value)
                  }}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                    selected
                      ? 'border-brand-500 bg-brand-50 text-brand-500'
                      : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                  )}
                >
                  {option.label}
                </button>
              )
            })}
          </div>

          {listQuery.isLoading ? <LoadingState rows={4} label="홍보 성과를 불러오는 중" /> : null}
          {listQuery.isError ? (
            <ErrorState
              error={listQuery.error}
              onRetry={() => {
                void listQuery.refetch()
              }}
            />
          ) : null}
          {!listQuery.isLoading && !listQuery.isError && conversionTracking ? (
            <PromotionPerformanceListTable
              items={items}
              conversionTracking={conversionTracking}
              onSelect={(item) => {
                navigate(`/venues/${venueId}/notices/${item.campaignId}`)
              }}
              hasNextPage={listQuery.hasNextPage ?? false}
              isFetchingNextPage={listQuery.isFetchingNextPage}
              onLoadMore={() => {
                void listQuery.fetchNextPage()
              }}
            />
          ) : null}
        </>
      ) : (
        <>
          {noticeReadStatusQuery.isLoading ? <LoadingState rows={1} label="공지 열람 현황을 불러오는 중" /> : null}
          {noticeReadStatusQuery.isError ? (
            <ErrorState
              error={noticeReadStatusQuery.error}
              onRetry={() => {
                void noticeReadStatusQuery.refetch()
              }}
            />
          ) : null}
          {noticeReadStatusQuery.data ? (
            <NoticeReadStatusSummaryCard data={noticeReadStatusQuery.data} venueId={venueId} />
          ) : null}
        </>
      )}
    </>
  )
}
